import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { API_BASE_URL } from '../services/api';
import { ArrowLeft, Users, DollarSign, ClipboardList, CheckCircle, Clock, AlertTriangle, AlertCircle, X, Phone, MapPin, Package, Calendar } from 'lucide-react';

const getProofImageUrl = (proofImage) => {
  if (!proofImage) return null;
  if (proofImage.startsWith('http')) return proofImage;
  return `${API_BASE_URL}${proofImage}`;
};

const getTimeElapsed = (dateStr) => {
  if (!dateStr) return { text: 'N/A', isOverdue: false };
  const created = new Date(dateStr);
  const now = new Date();
  const diffMs = now - created;
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    return { text: `${days}d ${remHours}h`, isOverdue: true };
  }
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  return { text: `${hours}h ${minutes}m`, isOverdue: false };
};

const StatCard = ({ title, value, color, subtext }) => (
  <div className={`bg-white p-4 rounded-xl shadow-sm border border-gray-100 border-l-4 ${color.replace('bg-', 'border-l-')}`}>
    <p className="text-xs text-gray-500 mb-1 truncate">{title}</p>
    <h3 className="text-xl font-bold text-gray-800">{value}</h3>
    {subtext && <p className="text-[10px] text-gray-400 mt-1 truncate">{subtext}</p>}
  </div>
);

const CustomerHistory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('collections');

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/customers/${id}/history/`);
        setData(res.data);
      } catch (err) {
        setError('Failed to load customer history');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [id]);

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-gray-500">Loading customer history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={() => navigate(-1)} className="text-blue-600 hover:text-blue-800 font-medium">
          Go Back
        </button>
      </div>
    );
  }

  const { customer, collections, complaints, summary } = data;

  const statusColors = {
    active: 'bg-green-100 text-green-700',
    pending: 'bg-blue-100 text-blue-700',
    expired: 'bg-yellow-100 text-yellow-700',
    suspended: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    churned: 'bg-red-100 text-red-700',
  };

  const complaintStatusColors = {
    open: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-yellow-100 text-yellow-700',
    resolved: 'bg-green-100 text-green-700',
    closed: 'bg-gray-100 text-gray-700',
  };

  const priorityColors = {
    low: 'bg-gray-100 text-gray-600',
    medium: 'bg-blue-100 text-blue-600',
    high: 'bg-orange-100 text-orange-600',
    critical: 'bg-red-100 text-red-600',
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 text-sm font-medium"
      >
        <ArrowLeft size={18} />
        Back
      </button>

      {/* Customer Info Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <span className="text-xl font-bold text-blue-600">
                {(customer.name || customer.username || '?')[0].toUpperCase()}
              </span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{customer.name || customer.username}</h1>
              <p className="text-sm text-gray-500">@{customer.username}</p>
            </div>
          </div>
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${statusColors[customer.status] || 'bg-gray-100 text-gray-700'}`}>
            {customer.status?.toUpperCase()}
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <Phone size={14} className="text-gray-400" />
            <span className="text-sm text-gray-700">{customer.phone || 'N/A'}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-gray-400" />
            <span className="text-sm text-gray-700">{customer.area_name || 'N/A'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Package size={14} className="text-gray-400" />
            <span className="text-sm text-gray-700">{customer.package_name || 'N/A'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-gray-400" />
            <span className="text-sm text-gray-700">
              Exp: {customer.expiry_date ? new Date(customer.expiry_date).toLocaleDateString() : 'N/A'}
            </span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatCard title="Total Collected" value={`PKR ${summary.total_collected.toLocaleString()}`} color="bg-green-500" subtext={`${summary.collection_count} payments`} />
        <StatCard title="Collections" value={summary.collection_count} color="bg-blue-500" />
        <StatCard title="Open Complaints" value={summary.open_complaints + summary.in_progress_complaints} color="bg-yellow-500" />
        <StatCard title="Resolved" value={summary.resolved_complaints} color="bg-green-500" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab('collections')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'collections' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Collections ({collections.length})
        </button>
        <button
          onClick={() => setActiveTab('complaints')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'complaints' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Complaints ({complaints.length})
        </button>
      </div>

      {/* Collections Tab */}
      {activeTab === 'collections' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {collections.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="font-medium">No collections found</p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                      <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Method</th>
                      <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Officer</th>
                      <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Transaction ID</th>
                      <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Proof</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {collections.map(c => (
                      <tr key={c.id} className="hover:bg-gray-50">
                        <td className="px-6 py-3 text-sm text-gray-700">
                          {new Date(c.collected_at).toLocaleDateString()} <span className="text-xs text-gray-400">{new Date(c.collected_at).toLocaleTimeString()}</span>
                        </td>
                        <td className="px-6 py-3 text-sm font-semibold text-green-700">
                          PKR {parseFloat(c.amount_collected).toLocaleString()}
                        </td>
                        <td className="px-6 py-3">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            {c.payment_method}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-700">{c.officer_name || 'N/A'}</td>
                        <td className="px-6 py-3 text-xs text-gray-500 font-mono">{c.transaction_id || '-'}</td>
                        <td className="px-6 py-3">
                          {getProofImageUrl(c.proof_image) ? (
                            <a href={getProofImageUrl(c.proof_image)} target="_blank" rel="noreferrer" className="inline-block">
                              <img src={getProofImageUrl(c.proof_image)} alt="Payment proof" className="h-12 w-12 rounded-lg object-cover border border-gray-200 hover:opacity-80" />
                            </a>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Mobile cards */}
              <div className="sm:hidden divide-y divide-gray-100">
                {collections.map(c => (
                  <div key={c.id} className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-green-700">PKR {parseFloat(c.amount_collected).toLocaleString()}</span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{c.payment_method}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(c.collected_at).toLocaleDateString()} {new Date(c.collected_at).toLocaleTimeString()}
                    </div>
                    {c.officer_name && <div className="text-xs text-gray-500 mt-1">By: {c.officer_name}</div>}
                    {c.transaction_id && <div className="text-xs text-gray-400 font-mono mt-1">TXN: {c.transaction_id}</div>}
                    {getProofImageUrl(c.proof_image) && (
                      <a href={getProofImageUrl(c.proof_image)} target="_blank" rel="noreferrer" className="inline-block mt-3">
                        <img src={getProofImageUrl(c.proof_image)} alt="Payment proof" className="h-20 w-20 rounded-lg object-cover border border-gray-200" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Complaints Tab */}
      {activeTab === 'complaints' && (
        <div className="space-y-3">
          {complaints.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center text-gray-500">
              <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="font-medium">No complaints found</p>
            </div>
          ) : (
            complaints.map(c => {
              const time = getTimeElapsed(c.created_at);
              return (
                <div key={c.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm">{c.title}</p>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${complaintStatusColors[c.status]}`}>
                          {c.status?.replace('_', ' ')}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityColors[c.priority]}`}>
                          {c.priority}
                        </span>
                      </div>
                    </div>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${time.isOverdue ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                      {time.isOverdue && <AlertTriangle size={12} />}
                      <Clock size={12} />
                      {time.text}
                    </div>
                  </div>
                  {c.description && (
                    <p className="text-xs text-gray-500 mb-2 line-clamp-2">{c.description}</p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    {c.technician_name && <span>Tech: {c.technician_name}</span>}
                    <span>{new Date(c.created_at).toLocaleDateString()}</span>
                  </div>
                  {c.resolution_notes && (c.status === 'resolved' || c.status === 'closed') && (
                    <div className="mt-2 p-2 bg-green-50 rounded-lg text-xs text-green-700">
                      <span className="font-medium">Resolution:</span> {c.resolution_notes}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerHistory;
